import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import SegmentLeadForm from "@/components/SegmentLeadForm";

/**
 * SegmentPage — shared layout for the three segment landing pages
 * (High School / Collegiate / Professional). Keeps the visual language
 * consistent while each surface owns its headline + outcomes list.
 */
export default function SegmentPage({
  eyebrow, headline, subhead, tone, outcomes, focus, ctaLabel = "Request access",
  testId, secondaryLabel = "See maturity paths", secondaryHref = "/pricing",
  leadSegment, whitepaperLabel,
}) {
  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--ink-1)" }} data-testid={testId}>
      {/* NAV */}
      <header className="absolute top-0 left-0 right-0 z-30">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-5">
          <Logo size="md" variant="lockup" linkTo="/" dark />
          <nav className="hidden md:flex items-center gap-7 text-[13px] text-white/70">
            <Link to="/" className="hover:text-white">Home</Link>
            <Link to="/high-school" className="hover:text-white">High School</Link>
            <Link to="/college" className="hover:text-white">Collegiate</Link>
            <Link to="/professional" className="hover:text-white">Professional</Link>
            <Link to="/pricing" className="hover:text-white">Plans</Link>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-sm font-medium text-white/80 hover:text-white">Sign in</Link>
            <Link to="/signup">
              <Button className="rounded-full px-5 text-white red-glow" style={{ backgroundColor: "var(--brand-secondary)" }}>
                Request access <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden pt-40 pb-20 md:pt-48 md:pb-28"
        style={{
          backgroundImage:
            `radial-gradient(ellipse at 20% -10%, ${tone}25, transparent 55%),` +
            "radial-gradient(ellipse at 90% 10%, rgba(56,189,248,0.08), transparent 55%)," +
            "linear-gradient(180deg, #05090F 0%, #0A1424 100%)",
        }}
        data-testid={`${testId}-hero`}
      >
        <div className="absolute inset-0 opacity-[0.05] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
            backgroundSize: "56px 56px",
          }}
        />
        <div className="relative max-w-6xl mx-auto px-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border text-[11px] tracking-[0.2em] font-bold uppercase"
            style={{ borderColor: `${tone}55`, backgroundColor: `${tone}18`, color: tone }}
            data-testid={`${testId}-eyebrow`}
          >
            {eyebrow}
          </div>
          <h1 className="mt-6 font-display font-black tracking-tighter leading-[0.92] text-5xl md:text-7xl text-white"
            data-testid={`${testId}-headline`}
          >
            {headline}
          </h1>
          <p className="mt-8 text-base md:text-xl text-white/75 max-w-3xl leading-relaxed">
            {subhead}
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Link to="/signup">
              <Button size="lg" className="rounded-full px-8 py-7 text-base text-white red-glow font-bold" style={{ backgroundColor: "var(--brand-secondary)" }}
                data-testid={`${testId}-cta-primary`}
              >
                {ctaLabel} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to={secondaryHref}>
              <Button size="lg" variant="outline" className="rounded-full px-8 py-7 text-base border-2 border-white/25 text-white bg-white/5 hover:bg-white/10 backdrop-blur">
                {secondaryLabel}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FOCUS strip */}
      <section className="py-16 md:py-20" data-testid={`${testId}-focus`}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-[11px] tracking-[0.35em] uppercase font-bold text-white/40">Focus areas</div>
          <div className="mt-6 grid md:grid-cols-3 lg:grid-cols-5 gap-3">
            {focus.map((f) => (
              <div key={f}
                className="rounded-lg border py-3 px-4 text-center text-[13px] font-semibold text-white/80"
                style={{ borderColor: "rgba(255,255,255,0.10)", backgroundColor: "rgba(15,30,54,0.5)" }}
              >
                {f}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Executive outcomes */}
      <section className="py-16 md:py-24" style={{ backgroundColor: "var(--ink-0)" }} data-testid={`${testId}-outcomes`}>
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-[11px] tracking-[0.35em] uppercase font-bold text-white/40">Executive outcomes</div>
          <h2 className="mt-3 font-display font-black text-3xl md:text-5xl tracking-tighter text-white leading-[0.95]">
            Measurable outcomes.<br />
            <span style={{ color: tone }}>Not feature lists.</span>
          </h2>
          <ul className="mt-10 grid md:grid-cols-2 gap-3">
            {outcomes.map((o) => (
              <li key={o.title}
                className="rounded-xl border p-5"
                style={{ backgroundColor: "rgba(15,30,54,0.5)", borderColor: "rgba(255,255,255,0.08)" }}
              >
                <div className="flex items-start gap-3">
                  <Check className="h-5 w-5 mt-0.5 shrink-0" style={{ color: tone }} />
                  <div>
                    <div className="text-base font-display font-black text-white tracking-tight">{o.title}</div>
                    <div className="mt-1 text-sm text-white/60 leading-relaxed">{o.body}</div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Final CTA — email capture */}
      <section className="py-20 md:py-28" data-testid={`${testId}-final-cta`}>
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="font-display font-black text-3xl md:text-5xl tracking-tighter text-white leading-[0.95] text-center mb-10">
            Ready to give your program<br />
            <span style={{ color: tone }}>one intelligence layer?</span>
          </h2>
          {leadSegment && (
            <SegmentLeadForm
              segment={leadSegment}
              tone={tone}
              whitepaperLabel={whitepaperLabel || "Program Intelligence Whitepaper"}
              testId={`${testId}-lead-form`}
            />
          )}
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Link to="/enterprise/contact" data-testid={`${testId}-enterprise-cta`}>
              <Button size="lg" variant="outline" className="rounded-full px-8 py-6 text-base border-2 border-white/25 text-white bg-white/5 hover:bg-white/10">
                Or talk to enterprise
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
